import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, Search } from 'lucide-react';
import { format } from 'date-fns';
import { auditApi } from '@/shared/adminApi';

export function AuditLogPage() {
  const [filters, setFilters] = useState<{
    entity_type?: string; action?: string; actor_id?: string;
  }>({});

  const { data, isLoading } = useQuery({
    queryKey: ['audit', filters],
    queryFn: () => auditApi.list({ ...filters, limit: 100 }),
    refetchInterval: 30_000,
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-axis-ink inline-flex items-center gap-2">
          <History className="w-6 h-6 text-axis-maroon" />
          Audit Log
        </h1>
        <p className="text-sm text-axis-ash mt-1">
          Append-only record of every state-changing action. PII redacted at write-time.
          Retention: 7 years (RBI).
        </p>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <FilterInput
            label="Entity type"
            placeholder="recon_job, doc_job, …"
            onChange={(v) => setFilters({ ...filters, entity_type: v })}
          />
          <FilterInput
            label="Action"
            placeholder="recon.job.created, …"
            onChange={(v) => setFilters({ ...filters, action: v })}
          />
          <FilterInput
            label="Actor ID"
            placeholder="user-abc"
            onChange={(v) => setFilters({ ...filters, actor_id: v })}
          />
        </div>
      </div>

      {/* Results */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-axis-cloud-100 text-axis-ash text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Actor</th>
              <th className="text-left px-4 py-3">Action</th>
              <th className="text-left px-4 py-3">Entity</th>
              <th className="text-left px-4 py-3">Request ID</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-axis-cloud-200">
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-axis-ash">Loading…</td></tr>
            ) : !data?.rows?.length ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-axis-ash">
                <Search className="w-6 h-6 mx-auto mb-2 text-axis-cloud-200" />
                No audit entries match these filters.
              </td></tr>
            ) : data.rows.map((entry) => (
              <tr key={entry.id} className="hover:bg-axis-cloud-50">
                <td className="px-4 py-3 text-xs text-axis-ash font-mono">
                  {format(new Date(entry.created_at), 'd MMM HH:mm:ss.SSS')}
                </td>
                <td className="px-4 py-3 text-xs">
                  <div>{entry.actor_id}</div>
                  <div className="text-axis-ash">{entry.actor_role}</div>
                </td>
                <td className="px-4 py-3 text-sm font-medium">{entry.action}</td>
                <td className="px-4 py-3 text-xs">
                  <div className="text-axis-ash">{entry.entity_type}</div>
                  <div className="font-mono">{entry.entity_id.slice(0, 16)}…</div>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-axis-ash">
                  {entry.request_id ? entry.request_id.slice(0, 12) + '…' : '—'}
                </td>
                <td className="px-4 py-3">
                  <details className="text-xs">
                    <summary className="cursor-pointer text-axis-maroon">Details</summary>
                    <pre className="mt-2 p-2 bg-axis-cloud-50 rounded overflow-x-auto max-w-md">
                      {JSON.stringify({
                        before: entry.before_snapshot,
                        after: entry.after_snapshot,
                        metadata: entry.metadata,
                      }, null, 2)}
                    </pre>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterInput({ label, placeholder, onChange }: {
  label: string; placeholder: string; onChange: (v?: string) => void;
}) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-axis-ash mb-1">{label}</div>
      <input
        type="text"
        placeholder={placeholder}
        className="w-full px-3 py-1.5 text-sm border border-axis-cloud-200 rounded focus:ring-2 focus:ring-axis-maroon focus:outline-none"
        onChange={(e) => onChange(e.target.value.trim() || undefined)}
      />
    </label>
  );
}
